import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '@components/Button';
import Modal from '@components/Modal';
import { supabase } from '@lib/supabase';
import { useAuth } from '@lib/useAuth';
import { listVisionBoardTemplates } from '@lib/visionBoardTemplates';
import type { VisionBoardTemplate } from '@lib/api';

type VisionBoardImage = {
  id: string;
  title: string;
  image_url: string | null;
  preview_image_url: string | null;
  created_at: string | null;
  filename: string;
  preview_filename: string;
  is_template?: boolean;
};

const formatTimestamp = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const broadcastVisionBoardDeleted = (id: string) => {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel('vision_board_images');
  channel.postMessage({ type: 'vision_board_deleted', id });
  channel.close();
};

function VisionBoards() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [boards, setBoards] = useState<VisionBoardImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeBoard, setActiveBoard] = useState<VisionBoardImage | null>(null);
  const [previewBoard, setPreviewBoard] = useState<VisionBoardImage | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const emptyState = useMemo(
    () => ({
      title: "You haven't saved any vision boards yet.",
      subtitle: 'Create one to use it as a breathing background.',
    }),
    []
  );

  useEffect(() => {
    let active = true;

    if (!user?.id) {
      setBoards([]);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    (async () => {
      try {
        setLoading(true);

        // Fetch templates first
        let templates: VisionBoardTemplate[] = [];
        try {
          templates = await listVisionBoardTemplates(false);
        } catch (err) {
          console.error('Failed to fetch vision board templates', err);
        }

        const { data, error: loadError } = await supabase
          .from('vision_board_images')
          .select(
            'id, title, image_url, preview_image_url, created_at, filename, preview_filename'
          )
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (!active) return;

        if (loadError) {
          setError('Unable to load vision boards right now. Please try again.');
          setBoards([]);
          return;
        }

        const userBoards = (data as VisionBoardImage[]) || [];
        const templateBoards: VisionBoardImage[] = templates.map((t) => ({
          id: t.id,
          title: t.title,
          image_url: t.image_url,
          preview_image_url: t.preview_image_url,
          created_at: t.created_at,
          filename: '',
          preview_filename: '',
          is_template: true,
        }));

        setBoards([...templateBoards, ...userBoards]);
        setError(null);
      } catch (_error) {
        if (!active) return;
        setError('Unable to load vision boards right now. Please try again.');
        setBoards([]);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    const stateError = (location.state as { error?: string } | null)?.error;
    if (!stateError) return;
    setError(stateError);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setActiveBoard(null);
    setDeleteError(null);
  };

  const openPreview = (board: VisionBoardImage) => {
    setPreviewBoard(board);
  };

  const closePreview = () => {
    setPreviewBoard(null);
  };

  const handleDelete = async () => {
    if (!activeBoard || isDeleting) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const baseName =
        activeBoard.filename.split('/').pop() || activeBoard.filename;
      const previewName = baseName.replace(/\.jpg$/i, '-preview.jpg');
      const previewPath =
        activeBoard.preview_filename || `vision-board/previews/${previewName}`;
      const { error: storageError } = await supabase.storage
        .from('vision_board')
        .remove([activeBoard.filename, previewPath]);

      if (storageError) {
        setDeleteError(
          "We couldn't delete this vision board. Please try again."
        );
        return;
      }

      const { error: dbError } = await supabase
        .from('vision_board_images')
        .delete()
        .eq('id', activeBoard.id);

      if (dbError) {
        setDeleteError(
          "We couldn't delete this vision board. Please try again."
        );
        return;
      }

      setBoards((prev) => prev.filter((item) => item.id !== activeBoard.id));
      broadcastVisionBoardDeleted(activeBoard.id);
      setActiveBoard(null);
    } catch (_error) {
      setDeleteError("We couldn't delete this vision board. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="mx-auto max-w-5xl pt-16 text-center">
        <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
          My Vision Boards
        </h1>
        <p className="text-lg text-gray-600">
          Manage your saved vision board backgrounds.
        </p>
      </div>

      <div className="relative mx-auto max-w-6xl px-4 py-12">
        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
                  />
                ))}
              </div>
            ) : boards.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                <div>{emptyState.title}</div>
                <div className="mt-1 text-sm text-gray-500">
                  {emptyState.subtitle}
                </div>
                <div className="mt-4 flex justify-center">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="item-selected"
                    onClick={() => navigate('/vision-board')}
                  >
                    Create a Vision Board
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-4 lg:hidden">
                  {boards.map((board) => (
                    <div
                      key={board.id}
                      className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex items-start gap-4">
                          <button
                            type="button"
                            onClick={() => openPreview(board)}
                            aria-label="View vision board preview"
                            className="h-20 w-28 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                          >
                            <img
                              src={
                                board.preview_image_url ?? board.image_url ?? ''
                              }
                              alt={board.title || 'Vision board'}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          </button>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-lg font-semibold text-gray-900">
                                {board.title || 'Untitled vision board'}
                              </p>
                              {board.is_template && (
                                <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-bold text-primary-700">
                                  Template
                                </span>
                              )}
                            </div>
                            {board.created_at && (
                              <p className="text-sm text-gray-500">
                                {formatTimestamp(board.created_at)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {!board.is_template && (
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => {
                                setActiveBoard(board);
                                setDeleteError(null);
                              }}
                              className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden overflow-auto rounded-2xl border border-gray-100 bg-white/90 lg:block">
                  <table className="min-w-full text-left text-sm">
                    <thead className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3">Preview</th>
                        <th className="px-4 py-3">Title</th>
                        <th className="px-4 py-3">Created</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boards.map((board) => (
                        <tr
                          key={board.id}
                          className="border-t border-gray-100 hover:bg-indigo-50"
                        >
                          <td className="px-4 py-4 align-top">
                            <button
                              type="button"
                              onClick={() => openPreview(board)}
                              aria-label="View vision board preview"
                              className="h-16 w-24 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                            >
                              <img
                                src={
                                  board.preview_image_url ??
                                  board.image_url ??
                                  ''
                                }
                                alt={board.title || 'Vision board'}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            </button>
                          </td>
                          <td className="px-4 py-4 align-top text-sm font-semibold text-gray-900">
                            <div className="flex items-center gap-2">
                              {board.title || 'Untitled vision board'}
                              {board.is_template && (
                                <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-bold text-primary-700">
                                  Template
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top text-sm text-gray-700">
                            {formatTimestamp(board.created_at) || '—'}
                          </td>
                          <td className="px-4 py-4 text-right align-top">
                            {!board.is_template && (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => {
                                  setActiveBoard(board);
                                  setDeleteError(null);
                                }}
                                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                              >
                                Delete
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {activeBoard && (
        <Modal title="Delete Vision Board" onClose={closeDeleteModal}>
          <p className="text-sm text-gray-700">
            Are you sure you want to delete this vision board? This action
            cannot be undone.
          </p>
          {deleteError && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {deleteError}
            </div>
          )}
          <div className="mt-6 flex items-center justify-end gap-3">
            <Button type="button" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </Modal>
      )}

      {previewBoard && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Vision board preview"
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
          onClick={closePreview}
        >
          <div
            className="absolute inset-0 bg-black/85 backdrop-blur-sm"
            aria-hidden="true"
          />
          <div
            className="relative z-10 w-full max-w-5xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closePreview}
              aria-label="Close preview"
              className="absolute right-2 top-2 rounded-full bg-white/90 p-2 text-gray-700 shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="h-4 w-4"
              >
                <path
                  d="M6 6l12 12M6 18L18 6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <img
              src={
                previewBoard.image_url ?? previewBoard.preview_image_url ?? ''
              }
              alt={
                previewBoard.title
                  ? `${previewBoard.title} preview`
                  : 'Vision board preview'
              }
              className="mx-auto max-h-[80vh] w-full rounded-2xl border border-white/40 object-contain shadow-2xl"
              loading="lazy"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default VisionBoards;
